import { Component, OnDestroy } from '@angular/core';
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonItem,
  IonInput,
  IonIcon,
} from '@ionic/angular/standalone';
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { FormsModule } from '@angular/forms';
import { Platform } from '@ionic/angular';
import { LangService } from '../services/language/language.service';
import { ChannelsService } from '../services/channel/channels.service';
import { addIcons } from 'ionicons';
import { sunnyOutline, moonOutline } from 'ionicons/icons';

addIcons({ sunnyOutline, moonOutline });

@Component({
  selector: 'app-home',
  templateUrl: 'home.page.html',
  styleUrls: ['home.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonButton,
    IonButtons,
    IonItem,
    IonInput,
    IonIcon,
  ],
})
export class HomePage implements OnDestroy {
  scanning = false;
  scannedResult: string | null = null;
  codeReader: BrowserMultiFormatReader | null = null;
  hasNavigated = false;
  controls: IScannerControls | null = null;
  manualCode = '';
  mode: 'idle' | 'scan' | 'manual' = 'idle';
  isDarkMode = false;
  private cameraStream: MediaStream | null = null;

  constructor(
    private router: Router,
    private alertController: AlertController,
    private platform: Platform,
    private langService: LangService,
    private channelsService: ChannelsService
  ) {}

  ionViewWillEnter() {
    this.initializeTheme();
  }

  private initializeTheme() {
    const savedTheme = localStorage.getItem('themePreference');
    if (savedTheme === 'dark') {
      this.applyTheme(true);
      return;
    }

    if (savedTheme === 'light') {
      this.applyTheme(false);
      return;
    }

    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    this.applyTheme(prefersDark);
  }

  onThemeToggle(enabled: boolean) {
    this.applyTheme(enabled);
    localStorage.setItem('themePreference', enabled ? 'dark' : 'light');
  }

  private applyTheme(enabled: boolean) {
    this.isDarkMode = enabled;
    document.body.classList.toggle('dark', enabled);
    document.body.classList.toggle('light', !enabled);
  }

  chooseScan() {
    this.stopScan();
    this.hasNavigated = false; // reset so startScan() guard doesn't block
    this.mode = 'scan';
    this.scanning = true;
    setTimeout(() => this.startScan(), 200);
  }

  chooseManual() {
    this.stopScan();
    this.mode = 'manual';
  }

  async startScan() {
    if (this.hasNavigated) return;

    try {
      // Reuse existing stream to avoid re-requesting camera permission
      if (!this.cameraStream || !this.cameraStream.active) {
        this.cameraStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
        });
      }

      // Always create a fresh reader — reusing after stop() causes ZXing stale state
      this.codeReader = new BrowserMultiFormatReader();

      this.controls = await this.codeReader.decodeFromStream(
        this.cameraStream,
        'video-preview',
        async (result) => {
          if (result && !this.hasNavigated) {
            const text = result.getText();

            const isFullURL = /^https?:\/\//i.test(text);
            const showURLMatch = /\/show\/([^\/\s]+)(?:\/([^\/\s]+))?/i.exec(text);

            if (isFullURL && showURLMatch) {
              const scannedCode = showURLMatch[1];
              const scannedLang = showURLMatch[2] || null;

              this.scannedResult = text;
              this.hasNavigated = true;
              this.stopScan();

              await this.navigateToChannel(scannedCode, scannedLang);
              return;
            }

            const isRawCode = /^[A-Z0-9]{10,20}$/.test(text.trim());
            if (isRawCode && this.scannedResult !== text) {
              this.scannedResult = text;
              this.hasNavigated = true;
              this.stopScan();
              await this.navigateToChannel(text.trim(), null);
            }
          }
        }
      );
    } catch (e) {
      console.error('Camera init error:', e);
      this.stopScan();
    }
  }

  async navigateToChannel(code: string, lang: string | null = null) {
    try {
      const channelInfo = await this.channelsService.getChannelByCode(code);
      if (channelInfo?.externalUrl) {
        await this.router.navigate([`/show/${code}`]);
        return;
      }
    } catch (e) {
      console.warn('Could not fetch channel info, falling back to language selector:', e);
    }

    // If lang was embedded in the QR URL, navigate directly without prompting
    if (lang) {
      await this.router.navigate([`/show/${code}/${lang}`]);
      return;
    }

    const selectedLang = await this.presentLanguageSelector(code);
    if (selectedLang) {
      await this.router.navigate([`/show/${code}/${selectedLang}`]);
    } else {
      console.log('Language not selected.');
    }
  }

  async presentLanguageSelector(scannedCode: string): Promise<string | null> {
    let selectedLang: string | null = null;

    const langs = await this.langService.getLanguages(scannedCode);

    if (langs.length === 1) {
      return langs[0].code;
    }

    const alert = await this.alertController.create({
      header: 'Select Language',
      cssClass: 'language-alert',
      inputs: langs.map((lang) => ({
        label: lang.name,
        type: 'radio',
        value: lang.code,
      })),
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
            selectedLang = null;
          },
        },
        {
          text: 'OK',
          handler: (data) => {
            if (!data) {
              this.alertController
                .create({
                  header: 'Language Required',
                  message: 'Please select a language before continuing.',
                  buttons: ['OK'],
                })
                .then((a) => a.present());

              return false;
            }

            selectedLang = data;
            return true;
          },
        },
      ],
    });

    await alert.present();
    await alert.onDidDismiss();
    return selectedLang;
  }

  stopScan() {
    this.scanning = false;
    this.mode = 'idle';
    this.scannedResult = null;

    if (this.controls) {
      this.controls.stop();
      this.controls = null;
    }

    // Keep cameraStream alive — stopping tracks forces browser to re-ask permission
  }

  ionViewWillLeave() {
    this.stopScan();
    this.releaseCameraStream();
  }

  ngOnDestroy() {
    this.releaseCameraStream();
  }

  private releaseCameraStream() {
    if (this.cameraStream) {
      this.cameraStream.getTracks().forEach((t) => t.stop());
      this.cameraStream = null;
    }
  }

  async handleManualCode() {
    const trimmed = this.manualCode.trim();

    // Support full URL format: https://sravyaa.in/show/CODE or https://sravyaa.in/show/CODE/lang
    const urlMatch = /\/show\/([^\/\s]+)(?:\/([^\/\s]+))?/i.exec(trimmed);
    if (urlMatch) {
      const code = urlMatch[1].toUpperCase();
      const lang = urlMatch[2] || null;
      this.manualCode = '';
      await this.navigateToChannel(code, lang);
      return;
    }

    const trimmedCode = trimmed.toUpperCase();
    if (!trimmedCode || !trimmedCode.startsWith('C')) {
      const alert = await this.alertController.create({
        header: 'Invalid Code',
        message: 'Please enter a valid code!!',
        buttons: ['OK'],
      });
      await alert.present();
      return;
    }

    this.manualCode = '';
    await this.navigateToChannel(trimmedCode, null);
  }
}
